import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from '@/components/ui/pagination';

// Icons
import { Users, UserPlus, Mail, Shield, Copy, Clock, CheckCircle, XCircle, AlertTriangle, Search, Edit, Link2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagement = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  
  // États pour recherche, filtrage et pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;
  
  // Dialog states
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [linkParentDialogOpen, setLinkParentDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [availableParents, setAvailableParents] = useState([]);
  
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: '',
    message: '',
    expires_days: 7
  });

  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    new_password: ''
  });

  const availableRoles = [
    { value: 'PARENT', label: 'Parent', description: 'Parent d\'élève' },
    { value: 'TEACHER', label: 'Enseignant(e)', description: 'Professeur' },
    { value: 'TREASURER', label: 'Trésorier', description: 'Gestion financière et facturation' },
    { value: 'SECRETARY', label: 'Secrétaire', description: 'Gestion administrative et élèves' },
    { value: 'LIFE_COUNSELOR', label: 'CPE', description: 'Conseiller Principal d\'Éducation' },
    { value: 'NURSE', label: 'Infirmier/ère', description: 'Personnel médical' },
    { value: 'LIBRARIAN', label: 'Documentaliste', description: 'Responsable CDI' },
    { value: 'ACCOUNTANT', label: 'Comptable', description: 'Gestion financière' },
    { value: 'PRINCIPAL', label: 'Directeur/trice', description: 'Direction d\'établissement' },
    { value: 'ADMIN', label: 'Administrateur', description: 'Administrateur système' }
  ];

  const statusColors = {
    PENDING: { color: 'badge-warning', icon: Clock, label: 'En attente' },
    ACCEPTED: { color: 'badge-success', icon: CheckCircle, label: 'Acceptée' },
    EXPIRED: { color: 'badge-danger', icon: XCircle, label: 'Expirée' },
    CANCELLED: { color: 'badge-secondary', icon: XCircle, label: 'Annulée' }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const promises = [
        axios.get(`${API}/users`),
        axios.get(`${API}/invitations`),
        axios.get(`${API}/admin/users-management`)
      ];

      const [usersRes, invitationsRes, statsRes] = await Promise.all(promises);

      setUsers(usersRes.data);
      setInvitations(invitationsRes.data);
      setStatistics(statsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const inviteData = {
        email: inviteForm.email,
        role: inviteForm.role,
        message: inviteForm.message || null,
        expires_at: new Date(Date.now() + inviteForm.expires_days * 24 * 60 * 60 * 1000).toISOString()
      };

      const response = await axios.post(`${API}/invitations`, inviteData);

      setInvitations(prev => [response.data, ...prev]);
      toast.success('Invitation envoyée avec succès');
      resetInviteForm();
      setInviteDialogOpen(false);
      
      // Copier le code d'invitation dans le presse-papiers
      const inviteCode = response.data.token;
      navigator.clipboard.writeText(inviteCode);
      toast.success(`Code d'invitation copié: ${inviteCode}`);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erreur lors de l\'envoi de l\'invitation';
      toast.error(errorMessage);
      console.error('Error sending invitation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (userToEdit) => {
    setSelectedUser(userToEdit);
    setEditForm({
      first_name: userToEdit.first_name || '',
      last_name: userToEdit.last_name || '',
      email: userToEdit.email || '',
      phone: userToEdit.phone || '',
      address: userToEdit.address || '',
      new_password: '' // Toujours vide par défaut
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.put(`${API}/users/${selectedUser.id}`, editForm);
      
      // Mettre à jour la liste des utilisateurs
      setUsers(prev => prev.map(u => u.id === selectedUser.id ? response.data : u));
      
      toast.success('Profil utilisateur mis à jour avec succès');
      setEditDialogOpen(false);
      setSelectedUser(null);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la mise à jour du profil';
      toast.error(errorMessage);
      console.error('Error updating user:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette invitation ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/invitations/${invitationId}`);
      setInvitations(prev => prev.map(inv => 
        inv.id === invitationId ? { ...inv, status: 'CANCELLED' } : inv
      ));
      toast.success('Invitation annulée');
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
      console.error('Error cancelling invitation:', error);
    }
  };

  const copyInviteCode = (token) => {
    navigator.clipboard.writeText(token);
    toast.success(`Code d'invitation copié: ${token}`);
  };

  const resetInviteForm = () => {
    setInviteForm({
      email: '',
      role: '',
      message: '',
      expires_days: 7
    });
  };

  const handleOpenLinkParentDialog = async (student) => {
    setSelectedStudent(student);
    setLinkParentDialogOpen(true);
    
    // Charger la liste des parents disponibles
    try {
      const response = await axios.get(`${API}/users?role=PARENT`);
      setAvailableParents(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des parents');
      console.error('Error fetching parents:', error);
    }
  };

  const handleLinkParent = async (parentId) => {
    if (!selectedStudent) return;
    
    try {
      setLoading(true);
      await axios.post(`${API}/students/${selectedStudent.id}/parents`, {
        parent_id: parentId
      });
      
      toast.success('Parent lié avec succès à l\'étudiant');
      setLinkParentDialogOpen(false);
      setSelectedStudent(null);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la liaison du parent';
      toast.error(errorMessage);
      console.error('Error linking parent:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleLabel = (role) => {
    const roleInfo = availableRoles.find(r => r.value === role);
    return roleInfo ? roleInfo.label : role;
  };

  const getStatusInfo = (status) => {
    return statusColors[status] || statusColors.PENDING;
  };

  // Fonction de filtrage et recherche
  const getFilteredUsers = () => {
    return users.filter(u => {
      // Filtre par recherche (nom, prénom, email)
      const matchesSearch = searchTerm === '' || 
        u.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtre par rôle
      const matchesRole = roleFilter === 'ALL' || u.role === roleFilter;
      
      return matchesSearch && matchesRole;
    });
  };

  // Pagination
  const filteredUsers = getFilteredUsers();
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  // Réinitialiser la page quand le filtre change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter]);

  const canManageUsers = user.role === 'ADMIN' || user.role === 'PRINCIPAL';

  if (!canManageUsers) {
    return (
      <div className="p-6">
        <Card className="card">
          <CardContent className="pt-6">
            <div className="text-center">
              <Shield className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Accès restreint</h3>
              <p className="text-gray-600">
                Cette section est réservée aux administrateurs et directeurs.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading && users.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 fade-in" data-testid="user-management">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <Users className="w-6 h-6 mr-2 text-blue-600" />
            Gestion des Utilisateurs
          </h1>
          <p className="text-gray-600 mt-1">
            Gérez les utilisateurs et les invitations de l'établissement
          </p>
        </div>

        <div className="flex gap-3">
          <Link to="/dashboard/create-users">
            <Button 
              className="btn-secondary"
              variant="outline"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Créer des utilisateurs
            </Button>
          </Link>

          <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="invite-user-btn"
                onClick={() => { resetInviteForm(); setInviteDialogOpen(true); }}
                className="btn-primary"
              >
                <Mail className="w-4 h-4 mr-2" />
                Inviter un utilisateur
              </Button>
            </DialogTrigger>

            <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Inviter un nouvel utilisateur</DialogTitle>
              <DialogDescription>
                Envoyez une invitation sécurisée pour créer un compte avec un rôle spécifique
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleInviteSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="invite_email">Adresse email</Label>
                <Input
                  id="invite_email"
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="utilisateur@exemple.com"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="invite_role">Rôle</Label>
                <Select 
                  value={inviteForm.role} 
                  onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez un rôle" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        <div>
                          <div className="font-medium">{role.label}</div>
                          <div className="text-xs text-gray-500">{role.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="expires_days">Expiration (jours)</Label>
                <Select 
                  value={inviteForm.expires_days.toString()} 
                  onValueChange={(value) => setInviteForm({ ...inviteForm, expires_days: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 jour</SelectItem>
                    <SelectItem value="3">3 jours</SelectItem>
                    <SelectItem value="7">7 jours</SelectItem>
                    <SelectItem value="14">14 jours</SelectItem>
                    <SelectItem value="30">30 jours</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="invite_message">Message personnalisé (optionnel)</Label>
                <Textarea
                  id="invite_message"
                  value={inviteForm.message}
                  onChange={(e) => setInviteForm({ ...inviteForm, message: e.target.value })}
                  placeholder="Message d'accompagnement pour l'invitation..."
                  rows={3}
                />
              </div>

              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">Sécurité des invitations</p>
                    <p>Un code à 6 chiffres sera généré et copié automatiquement. 
                       Partagez-le uniquement avec la personne concernée.</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setInviteDialogOpen(false)}
                >
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="btn-primary"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {loading ? 'Envoi...' : 'Envoyer l\'invitation'}
                </Button>
              </div>
            </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Dialog pour modifier un utilisateur */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Modifier le profil utilisateur</DialogTitle>
              <DialogDescription>
                Modifiez les informations de {selectedUser?.first_name} {selectedUser?.last_name} ({getRoleLabel(selectedUser?.role)})
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_first_name">Prénom *</Label>
                  <Input
                    id="edit_first_name"
                    value={editForm.first_name}
                    onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_last_name">Nom *</Label>
                  <Input
                    id="edit_last_name"
                    value={editForm.last_name}
                    onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_email">Email *</Label>
                <Input
                  id="edit_email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_phone">Téléphone</Label>
                <Input
                  id="edit_phone"
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  placeholder="+224 6XX XX XX XX"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_address">Adresse</Label>
                <Textarea
                  id="edit_address"
                  value={editForm.address}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                  placeholder="Adresse complète"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_password">Nouveau mot de passe</Label>
                <PasswordInput
                  id="edit_password"
                  value={editForm.new_password}
                  onChange={(e) => setEditForm({ ...editForm, new_password: e.target.value })}
                  placeholder="Laisser vide pour ne pas modifier"
                />
                <p className="text-xs text-gray-500">
                  Minimum 6 caractères. Laissez ce champ vide si vous ne souhaitez pas modifier le mot de passe.
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setEditDialogOpen(false)}
                >
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="btn-primary"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  {loading ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog pour lier un parent à un étudiant */}
        <Dialog open={linkParentDialogOpen} onOpenChange={setLinkParentDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Lier un parent à l'étudiant</DialogTitle>
              <DialogDescription>
                Sélectionnez un parent à lier à {selectedStudent?.first_name} {selectedStudent?.last_name} 
                {selectedStudent?.student_number && ` (N° ${selectedStudent.student_number})`}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {availableParents.length > 0 ? (
                <div className="space-y-3">
                  {availableParents.map((parent) => (
                    <Card key={parent.id} className="card">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold">
                              {parent.first_name} {parent.last_name}
                            </h4>
                            <p className="text-sm text-gray-600">{parent.email}</p>
                            {parent.phone && (
                              <p className="text-sm text-gray-500">Tél: {parent.phone}</p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleLinkParent(parent.id)}
                            disabled={loading}
                            className="btn-primary"
                          >
                            <Link2 className="w-4 h-4 mr-2" />
                            Lier
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600">Aucun parent disponible</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Créez d'abord des comptes parents pour pouvoir les lier aux étudiants
                  </p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Statistics */}
      {Object.keys(statistics).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Utilisateurs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{statistics.total_users}</div>
              <p className="text-xs text-gray-600 mt-1">Comptes actifs</p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Élèves</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{statistics.users_by_role?.STUDENT || 0}</div>
              <p className="text-xs text-gray-600 mt-1">Comptes élèves</p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Enseignants</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">{statistics.users_by_role?.TEACHER || 0}</div>
              <p className="text-xs text-gray-600 mt-1">Professeurs</p>
            </CardContent>
          </Card>

          <Card className="card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Invitations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{statistics.pending_invitations}</div>
              <p className="text-xs text-gray-600 mt-1">En attente</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="users">Utilisateurs</TabsTrigger>
          <TabsTrigger value="invitations">Invitations</TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          {/* Barre de recherche et filtres */}
          <Card className="card">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Barre de recherche */}
                <div className="flex-1">
                  <Label htmlFor="search" className="sr-only">Rechercher</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="search"
                      type="text"
                      placeholder="Rechercher par nom, prénom ou email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Filtre par rôle */}
                <div className="w-full md:w-64">
                  <Label htmlFor="role-filter" className="sr-only">Filtrer par rôle</Label>
                  <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger id="role-filter">
                      <SelectValue placeholder="Tous les rôles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">Tous les rôles</SelectItem>
                      <SelectItem value="STUDENT">Élèves</SelectItem>
                      <SelectItem value="PARENT">Parents</SelectItem>
                      <SelectItem value="TEACHER">Enseignants</SelectItem>
                      <SelectItem value="ADMIN">Administrateurs</SelectItem>
                      <SelectItem value="PRINCIPAL">Directeurs</SelectItem>
                      <SelectItem value="TREASURER">Trésoriers</SelectItem>
                      <SelectItem value="SECRETARY">Secrétaires</SelectItem>
                      <SelectItem value="LIFE_COUNSELOR">CPE</SelectItem>
                      <SelectItem value="NURSE">Infirmiers</SelectItem>
                      <SelectItem value="LIBRARIAN">Documentalistes</SelectItem>
                      <SelectItem value="ACCOUNTANT">Comptables</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Compteur de résultats */}
              <div className="mt-4 text-sm text-gray-600">
                {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''} trouvé{filteredUsers.length > 1 ? 's' : ''}
                {(searchTerm || roleFilter !== 'ALL') && ` sur ${users.length} au total`}
              </div>
            </CardContent>
          </Card>

          {currentUsers.length > 0 ? (
            <div className="space-y-4">
              {currentUsers.map((user) => (
                <Card key={user.id} className="card">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="font-semibold text-lg">
                            {user.first_name} {user.last_name}
                          </h3>
                          <Badge className="badge-primary">
                            {getRoleLabel(user.role)}
                          </Badge>
                        </div>

                        <p className="text-sm text-gray-600 mb-1">
                          Email: {user.email}
                        </p>

                        {user.phone && (
                          <p className="text-sm text-gray-600 mb-1">
                            Téléphone: {user.phone}
                          </p>
                        )}

                        <div className="flex items-center justify-between text-xs text-gray-500 mt-3">
                          <span>
                            Inscrit le {new Date(user.created_at).toLocaleDateString('fr-FR')}
                          </span>
                          {user.last_login && (
                            <span>
                              Dernière connexion: {new Date(user.last_login).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Boutons d'action */}
                      <div className="ml-4 flex gap-2">
                        {/* Bouton Lier parent (uniquement pour les étudiants) */}
                        {user.role === 'STUDENT' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenLinkParentDialog(user)}
                            className="flex items-center gap-2"
                          >
                            <Link2 className="w-4 h-4" />
                            Lier parent
                          </Button>
                        )}
                        
                        {/* Bouton Modifier */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditUser(user)}
                          className="flex items-center gap-2"
                        >
                          <Edit className="w-4 h-4" />
                          Modifier
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm || roleFilter !== 'ALL' ? 'Aucun résultat' : 'Aucun utilisateur'}
              </h3>
              <p className="text-gray-600">
                {searchTerm || roleFilter !== 'ALL' 
                  ? 'Aucun utilisateur ne correspond à vos critères de recherche.'
                  : 'Les utilisateurs apparaîtront ici une fois inscrits.'
                }
              </p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                  
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    // Afficher seulement quelques pages autour de la page actuelle
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <PaginationItem key={pageNumber}>
                          <PaginationLink
                            onClick={() => setCurrentPage(pageNumber)}
                            isActive={currentPage === pageNumber}
                            className="cursor-pointer"
                          >
                            {pageNumber}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    } else if (
                      pageNumber === currentPage - 2 ||
                      pageNumber === currentPage + 2
                    ) {
                      return <PaginationItem key={pageNumber}><span className="px-2">...</span></PaginationItem>;
                    }
                    return null;
                  })}
                  
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </TabsContent>

        {/* Invitations Tab */}
        <TabsContent value="invitations" className="space-y-4">
          {invitations.length > 0 ? (
            <div className="space-y-4">
              {invitations.map((invitation) => {
                const statusInfo = getStatusInfo(invitation.status);
                const StatusIcon = statusInfo.icon;

                return (
                  <Card key={invitation.id} className="card">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-semibold">{invitation.email}</h3>
                            <Badge className={statusInfo.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusInfo.label}
                            </Badge>
                            <Badge className="badge-secondary">
                              {getRoleLabel(invitation.role)}
                            </Badge>
                          </div>

                          {invitation.message && (
                            <p className="text-sm text-gray-700 mb-2 italic">
                              Message: {invitation.message}
                            </p>
                          )}

                          <div className="text-xs text-gray-500 space-y-1">
                            <p>
                              Créée le {new Date(invitation.created_at).toLocaleDateString('fr-FR')}
                            </p>
                            <p>
                              Expire le {new Date(invitation.expires_at).toLocaleDateString('fr-FR')}
                            </p>
                            {invitation.accepted_at && (
                              <p className="text-green-600">
                                Acceptée le {new Date(invitation.accepted_at).toLocaleDateString('fr-FR')}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex space-x-2">
                          {invitation.status === 'PENDING' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => copyInviteCode(invitation.token)}
                              >
                                <Copy className="w-4 h-4 mr-1" />
                                Copier le code
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCancelInvitation(invitation.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Annuler
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <Mail className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune invitation</h3>
              <p className="text-gray-600 mb-6">
                Commencez par inviter des utilisateurs à rejoindre l'établissement.
              </p>
              <Button 
                onClick={() => { resetInviteForm(); setInviteDialogOpen(true); }}
                className="btn-primary"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Envoyer la première invitation
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UserManagement;